import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Modal,
  Textarea,
} from '@besiktning/ui'
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Camera,
  User,
  Car,
  CreditCard,
  Clock,
  Wrench,
  Image as ImageIcon,
  Loader2,
  Receipt,
} from 'lucide-react'
import { garageApi, carCareApi, formatCurrency, formatInTz, CANCELLATION_REASONS } from '@besiktning/ui'
import type { Booking, CarCareBooking, QuoteLineItem } from '@besiktning/ui'
import WorkOrderEditor, { lineItemsTotal } from '../components/WorkOrderEditor'
import type { LineItemForm } from '../components/WorkOrderEditor'
import { useAuthStore } from '../store/authStore'
import { trackWeb } from '../lib/analytics'
import { t } from '../i18n'

// Resolved at call time (not module load) so the brand locale is ready.
const STATUS_LABEL_KEYS: Record<string, string> = {
  pending_payment: 'jobDetail.statusPendingPayment',
  pending: 'jobDetail.statusPending',
  confirmed: 'jobDetail.statusConfirmed',
  completed: 'jobDetail.statusCompleted',
  cancelled: 'jobDetail.statusCancelled',
  pending_cancellation: 'jobDetail.statusPendingCancellation',
}

function statusLabel(status: string): string {
  const key = STATUS_LABEL_KEYS[status]
  return key ? t(key) : status
}

const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger'> = {
  pending_payment: 'warning',
  pending: 'warning',
  confirmed: 'default',
  completed: 'success',
  cancelled: 'danger',
  pending_cancellation: 'warning',
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentMembership } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const api = isCarCare ? carCareApi : garageApi

  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelNotes, setCancelNotes] = useState('')
  const [workNotes, setWorkNotes] = useState('')
  const [workNotesDirty, setWorkNotesDirty] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [lineItems, setLineItems] = useState<LineItemForm[]>([])
  const [lineItemsDirty, setLineItemsDirty] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: booking, isLoading } = useQuery({
    queryKey: ['job', id, isCarCare ? 'car_care' : 'garage'],
    queryFn: async () => {
      if (isCarCare) {
        return carCareApi.getBooking(id!)
      }
      return garageApi.getBooking(id!)
    },
    enabled: !!id,
    select: (data) => data as Booking | CarCareBooking,
  })

  // Sync work notes from server when first loaded
  React.useEffect(() => {
    if (booking && !workNotesDirty) {
      setWorkNotes((booking as any).work_notes ?? '')
    }
  }, [booking, workNotesDirty])

  // Same for the work order, until the garage starts editing it.
  React.useEffect(() => {
    if (booking && !lineItemsDirty) {
      setLineItems(((booking as any).line_items ?? []).map((item: QuoteLineItem) => ({
        id: item.id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        unit_type: item.unit_type,
      })))
    }
  }, [booking, lineItemsDirty])

  const updateMutation = useMutation({
    mutationFn: (data: { status?: string; work_notes?: string; cancellation_reason?: string }) =>
      api.updateBooking(id!, data),
    onSuccess: (_data, variables) => {
      // Staff workflow event: garage/car-care marked a job confirmed/completed/cancelled.
      if (variables.status) {
        trackWeb('job_status_changed', {
          status: variables.status,
          booking_id: id,
          membership_type: currentMembership?.type,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['job', id] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setShowCancelModal(false)
      setCancelReason('')
      setCancelNotes('')
      setWorkNotesDirty(false)
    },
  })

  const lineItemsMutation = useMutation({
    mutationFn: () =>
      garageApi.updateJobLineItems(id!, lineItems.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        unit_type: item.unit_type,
      }))),
    onSuccess: () => {
      setLineItemsDirty(false)
      queryClient.invalidateQueries({ queryKey: ['job', id] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
  })

  const aftercheckMutation = useMutation({
    mutationFn: (action: 'offer' | 'complete') =>
      action === 'offer' ? garageApi.offerAftercheck(id!) : garageApi.completeAftercheck(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
  })

  const approveCancellationMutation = useMutation({
    mutationFn: () => api.approveCancellation(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
  })

  const handleComplete = () => {
    updateMutation.mutate({ status: 'completed' })
  }

  const handleConfirm = () => {
    updateMutation.mutate({ status: 'confirmed' })
  }

  const handleCancel = () => {
    if (!cancelReason) return
    const reason = cancelNotes
      ? `${cancelReason}: ${cancelNotes}`
      : cancelReason
    updateMutation.mutate({ status: 'cancelled', cancellation_reason: reason })
  }

  const handleSaveWorkNotes = () => {
    updateMutation.mutate({ work_notes: workNotes })
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    setPhotoUploading(true)
    try {
      await api.uploadJobPhotos(id!, files)
      queryClient.invalidateQueries({ queryKey: ['job', id] })
    } finally {
      setPhotoUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!booking) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('jobDetail.notFound')}</p>
        <Button onClick={() => navigate('/jobs')} className="mt-4">{t('jobDetail.back')}</Button>
      </div>
    )
  }

  const b = booking as any
  const status: string = b.status
  const isPaid = b.payment_status === 'paid'
  const payAtGarage = b.payment_method === 'pay_at_garage'
  // Pay-at-garage never shows payment_status: paid until the job itself is
  // marked complete (the customer pays in person, not through Stripe) — so
  // gating "confirm"/"complete" on isPaid would make these jobs uncompletable.
  const paymentClearedToProceed = isPaid || payAtGarage

  // If payment is confirmed but booking status is still pending_payment, show the actual working status
  const displayStatus = (status === 'pending_payment' && isPaid)
    ? (isCarCare ? 'pending' : 'confirmed')
    : status

  const isActive = ['pending', 'confirmed', 'pending_payment'].includes(status)
  const isCancellable = !['cancelled', 'completed', 'pending_cancellation'].includes(status)
  const isCompletable = paymentClearedToProceed && ['confirmed', 'pending'].includes(status)
  const isPendingCancellation = status === 'pending_cancellation'
  const workOrderEditable = b.work_order_editable ?? false

  // Efterkontroll is a quote job thing only — a service booking has no work
  // order to put the fee on, so the backend never marks it offerable.
  const aftercheckStatus: string | null = b.aftercheck_status ?? null
  const showAftercheck = !isCarCare && (b.aftercheck_offerable || aftercheckStatus)
  const aftercheckPrice = formatCurrency(b.aftercheck_price ?? b.garage_aftercheck_price ?? 200)

  const customerName = b.user?.name || t('jobDetail.unknownCustomer')
  const customerPhone = b.user?.phone
  const customerEmail = b.user?.email

  const serviceName = isCarCare
    ? b.car_care_service?.name
    : b.title || t('jobDetail.booking')

  const vehicle = isCarCare
    ? b.vehicle
    : b.quote?.inspection_request?.vehicle

  const photoUrls: string[] = b.photo_urls ?? []

  const cancellationLabel = b.cancellation_reason
    ? (CANCELLATION_REASONS.find((r) => b.cancellation_reason?.startsWith(r.value))?.label ?? b.cancellation_reason)
    : null

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/jobs')} className="p-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 truncate">{serviceName}</h1>
          <p className="text-sm text-gray-500">
            {b.booking_reference ?? t('jobDetail.bookingNumber', { id: b.id })}
            {b.scheduled_at && (
              <> &middot; {formatInTz(b.scheduled_at, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</>
            )}
          </p>
        </div>
        <Badge variant={STATUS_VARIANTS[displayStatus] ?? 'default'}>
          {statusLabel(displayStatus)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Booking info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" /> {t('jobDetail.bookingInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">{t('jobDetail.service')}</dt>
                  <dd className="font-medium">{serviceName}</dd>
                </div>
                {b.car_care_service?.duration_minutes && (
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.estimatedTime')}</dt>
                    <dd className="font-medium">{b.car_care_service.duration_minutes} min</dd>
                  </div>
                )}
                {isCarCare && b.price != null && (
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.price')}</dt>
                    <dd className="font-medium">{formatCurrency(b.price)}</dd>
                  </div>
                )}
                {!isCarCare && (b.amount_incl_vat ?? b.quote?.price) != null && (
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.price')}</dt>
                    <dd className="font-medium">{formatCurrency(b.amount_incl_vat ?? b.quote.price)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500">{t('jobDetail.paymentStatusLabel')}</dt>
                  <dd className={`font-medium ${isPaid ? 'text-green-700' : 'text-amber-700'}`}>
                    {{ awaiting: t('jobDetail.payAwaiting'), paid: t('jobDetail.payPaid'), failed: t('jobDetail.payFailed'), refunded: t('jobDetail.payRefunded') }[b.payment_status as string] ?? b.payment_status ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('jobDetail.paymentMethodLabel')}</dt>
                  <dd className="font-medium">
                    {payAtGarage ? t('jobDetail.payAtGarage') : t('jobDetail.payKlarna')}
                  </dd>
                </div>
                {b.notes && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">{t('jobDetail.customerNote')}</dt>
                    <dd className="font-medium">{b.notes}</dd>
                  </div>
                )}
                {['cancelled', 'pending_cancellation'].includes(status) && cancellationLabel && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">{t('jobDetail.cancellationReasonLabel')}</dt>
                    <dd className="font-medium text-red-600">{cancellationLabel}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Work order — what the job is billed on. Car care jobs are
              fixed-price packages and have none. */}
          {!isCarCare && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" /> {t('jobDetail.workOrder')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {workOrderEditable ? (
                  <>
                    <WorkOrderEditor
                      hideTitle
                      items={lineItems}
                      onChange={(items) => {
                        setLineItems(items)
                        setLineItemsDirty(true)
                      }}
                    />
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={() => lineItemsMutation.mutate()}
                        disabled={!lineItemsDirty || lineItemsMutation.isPending}
                      >
                        {lineItemsMutation.isPending ? t('jobDetail.saving') : t('jobDetail.saveWorkOrder')}
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {payAtGarage && !isPaid ? t('jobDetail.workOrderHint') : t('jobDetail.workOrderPaidHint')}
                    </p>
                  </>
                ) : lineItems.length > 0 ? (
                  <dl className="space-y-2 text-sm">
                    {lineItems.map((item, index) => (
                      <div key={index} className="flex justify-between gap-4">
                        <dt className="text-gray-600">
                          {item.description} &times; {item.quantity} {item.unit_type}
                        </dt>
                        <dd className="font-medium">{formatCurrency(item.quantity * item.unit_price)}</dd>
                      </div>
                    ))}
                    <div className="flex justify-between gap-4 border-t border-gray-200 pt-2">
                      <dt className="font-medium">{t('jobDetail.total')}</dt>
                      <dd className="font-bold">{formatCurrency(lineItemsTotal(lineItems))}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-sm text-gray-500">{t('requestDetail.noWorkOrders')}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Work notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" /> {t('jobDetail.workDone')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder={t('jobDetail.workNotesPlaceholder')}
                value={workNotes}
                onChange={(e) => {
                  setWorkNotes(e.target.value)
                  setWorkNotesDirty(true)
                }}
                rows={5}
              />
              <div className="mt-3 flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveWorkNotes}
                  disabled={!workNotesDirty || updateMutation.isPending}
                  isLoading={updateMutation.isPending && workNotesDirty}
                >
                  {t('jobDetail.saveNote')}
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {t('jobDetail.noteVisibleHint')}
              </p>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" /> {t('jobDetail.photos')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {photoUrls.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                  {photoUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={url}
                        alt={t('jobDetail.imageAlt', { n: i + 1 })}
                        className="aspect-square object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 mb-4">
                  <Camera className="h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">{t('jobDetail.noPhotos')}</p>
                </div>
              )}
              {isPaid ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('jobDetail.uploading')}</>
                    ) : (
                      <><Camera className="h-4 w-4 mr-2" /> {t('jobDetail.addPhotos')}</>
                    )}
                  </Button>
                </>
              ) : (
                <p className="text-xs text-amber-600">
                  {t('jobDetail.photosAfterPayment')}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-400">
                {t('jobDetail.photosVisibleHint')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Efterkontroll */}
          {showAftercheck && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5" /> {t('jobDetail.aftercheckTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {aftercheckStatus === 'offered' && (
                  <p className="text-sm text-amber-700">{t('jobDetail.aftercheckWaiting')}</p>
                )}
                {aftercheckStatus === 'approved' && (
                  <>
                    <p className="text-sm text-green-700">
                      {t('jobDetail.aftercheckApproved', { price: aftercheckPrice })}
                    </p>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => aftercheckMutation.mutate('complete')}
                      isLoading={aftercheckMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {t('jobDetail.aftercheckComplete')}
                    </Button>
                  </>
                )}
                {aftercheckStatus === 'completed' && (
                  <p className="text-sm text-gray-600">{t('jobDetail.aftercheckDone')}</p>
                )}
                {aftercheckStatus === 'declined' && (
                  <p className="text-sm text-gray-600">{t('jobDetail.aftercheckDeclined')}</p>
                )}
                {b.aftercheck_offerable && (
                  <>
                    <p className="text-xs text-gray-500">{t('jobDetail.aftercheckOfferHint')}</p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => aftercheckMutation.mutate('offer')}
                      isLoading={aftercheckMutation.isPending}
                    >
                      <Car className="h-4 w-4 mr-2" />
                      {t('jobDetail.aftercheckOffer', { price: aftercheckPrice })}
                    </Button>
                    <p className="text-xs text-gray-400">{t('jobDetail.aftercheckPriceHint')}</p>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>{t('jobDetail.actions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isPendingCancellation && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <p className="text-xs font-medium text-amber-800 mb-2">
                    {t('jobDetail.cancellationRequestedNote')}
                  </p>
                  {b.cancellation_reason && (
                    <p className="text-xs text-amber-700 mb-2">{t('jobDetail.reasonLabel', { reason: b.cancellation_reason })}</p>
                  )}
                  <Button
                    size="sm"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => approveCancellationMutation.mutate()}
                    isLoading={approveCancellationMutation.isPending}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {t('jobDetail.approveCancellation')}
                  </Button>
                </div>
              )}
              {!paymentClearedToProceed && isCancellable && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <p className="text-xs font-medium text-amber-800">
                    {t('jobDetail.paymentNotConfirmedNote')}
                  </p>
                </div>
              )}
              {isCarCare && status === 'pending' && paymentClearedToProceed && (
                <Button
                  className="w-full"
                  onClick={handleConfirm}
                  isLoading={updateMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('jobDetail.confirmBooking')}
                </Button>
              )}
              {isCompletable && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleComplete}
                  isLoading={updateMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('jobDetail.markComplete')}
                </Button>
              )}
              {isCancellable && (
                <Button
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowCancelModal(true)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {t('jobDetail.cancel')}
                </Button>
              )}
              {!isActive && !isCancellable && (
                <p className="text-sm text-gray-500 text-center py-2">
                  {t('jobDetail.noActions')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Customer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" /> {t('jobDetail.customer')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">{t('jobDetail.name')}</dt>
                  <dd className="font-medium">{customerName}</dd>
                </div>
                {customerPhone && (
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.phone')}</dt>
                    <dd>
                      <a href={`tel:${customerPhone}`} className="text-blue-600 hover:underline font-medium">
                        {customerPhone}
                      </a>
                    </dd>
                  </div>
                )}
                {customerEmail && (
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.email')}</dt>
                    <dd>
                      <a href={`mailto:${customerEmail}`} className="text-blue-600 hover:underline font-medium truncate block">
                        {customerEmail}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Vehicle */}
          {vehicle && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5" /> {t('jobDetail.vehicle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.makeModel')}</dt>
                    <dd className="font-medium">
                      {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}
                    </dd>
                  </div>
                  {vehicle.year && (
                    <div>
                      <dt className="text-gray-500">{t('jobDetail.year')}</dt>
                      <dd className="font-medium">{vehicle.year}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.regNumber')}</dt>
                    <dd className="font-mono font-bold tracking-widest">{vehicle.reg_number}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          )}

          {/* Payment */}
          {b.payment_status && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> {t('jobDetail.payment')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.status')}</dt>
                    <dd className={`font-medium ${isPaid ? 'text-green-700' : 'text-amber-700'}`}>
                      {{ awaiting: t('jobDetail.payAwaiting'), paid: t('jobDetail.payPaid'), failed: t('jobDetail.payFailed'), refunded: t('jobDetail.payRefunded') }[b.payment_status as string] ?? b.payment_status}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">{t('jobDetail.paymentMethodLabel')}</dt>
                    <dd className="font-medium">
                      {payAtGarage ? t('jobDetail.payAtGarage') : t('jobDetail.payKlarna')}
                    </dd>
                  </div>
                  {(b.price ?? b.quote?.price) && (
                    <div>
                      <dt className="text-gray-500">{t('jobDetail.amount')}</dt>
                      <dd className="font-medium">{formatCurrency(b.price ?? b.quote?.price)}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false)
          setCancelReason('')
          setCancelNotes('')
        }}
        title={t('jobDetail.cancelJobTitle')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCancelModal(false)
                setCancelReason('')
                setCancelNotes('')
              }}
            >
              {t('jobDetail.cancelButton')}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancel}
              disabled={!cancelReason}
              isLoading={updateMutation.isPending}
            >
              {t('jobDetail.confirmCancellation')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('jobDetail.reason')} <span className="text-red-500">*</span>
            </label>
            <select
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
            >
              <option value="">{t('jobDetail.selectReason')}</option>
              {CANCELLATION_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('jobDetail.additionalInfo')} <span className="text-gray-400">{t('jobDetail.optional')}</span>
            </label>
            <Textarea
              placeholder={t('jobDetail.describeSituationPlaceholder')}
              value={cancelNotes}
              onChange={(e) => setCancelNotes(e.target.value)}
              rows={3}
            />
          </div>
          <p className="text-xs text-gray-400">
            {t('jobDetail.cancellationNotifHint')}
          </p>
        </div>
      </Modal>
    </div>
  )
}
